import axios from 'axios';

// Use relative URL — works on any domain since ingress routes /api to backend
const API_URL = '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // Include cookies for session auth
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    // Log when token is missing for authenticated routes (debugging)
    const isAuthRoute =
      config.url?.includes('/login') ||
      config.url?.includes('/register') ||
      config.url?.includes('/forgot-password') ||
      config.url?.includes('/reset-password') ||
      config.url?.includes('/magic-link') ||
      config.url?.includes('/email-code');

    if (!isAuthRoute) {
      console.debug('API request without token:', config.url);
    }
  }

  // Attach Accept-Language from persisted i18n choice so request-driven
  // emails (reset, magic link, invitations) render in the user's chosen
  // locale. Only ever forwards sv/en; unknown values collapse server-side.
  try {
    const lng =
      (typeof window !== 'undefined' &&
        window.localStorage.getItem('trainova.language')) ||
      'sv';

    const base = String(lng).toLowerCase().split('-')[0];

    config.headers['Accept-Language'] =
      base === 'en' ? 'en' : 'sv';
  } catch {
    /* noop — never fail a request over locale detection */
  }

  return config;
});

// Handle 401 errors - but only for actual auth failures, not network issues
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect to login for actual 401 responses from the server
    // Don't redirect for network errors or other transient issues
    if (error.response?.status === 401) {
      // Check if we're not already on the login page to prevent redirect loops
      const currentPath = window.location.pathname;

      if (
        currentPath !== '/login' &&
        currentPath !== '/register' &&
        currentPath !== '/reset-password'
      ) {
        // Verify the token is actually invalid by checking if we got a proper response
        const rawDetail = error.response?.data?.detail;
        const detail = typeof rawDetail === 'string' ? rawDetail : '';

        const isActualAuthError =
          detail === 'Not authenticated' ||
          detail === 'Could not validate credentials' ||
          detail.toLowerCase().includes('signature') ||
          detail.toLowerCase().includes('expired');

        if (isActualAuthError) {
          console.warn(
            'API interceptor: Clearing auth due to 401 -',
            detail
          );

          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password) =>
  api.post('/auth/login', { email, password });

export const register = (data) =>
  api.post('/auth/register', data);

export const getMe = () =>
  api.get('/auth/me');

export const switchTeam = (teamId) =>
  api.put(`/auth/switch-team/${teamId}`);

export const seedData = () =>
  api.post('/seed');

export const logout = () =>
  api.post('/auth/logout');

// Magic Link (Feb 2026 — primary login method)
export const requestMagicLink = (
  email,
  name,
  returnOrigin
) => {
  const origin =
    returnOrigin ||
    (
      typeof window !== 'undefined'
        ? window.location.origin
        : undefined
    );

  const body = { email };

  if (name) {
    body.name = name;
  }

  if (origin) {
    body.return_origin = origin;
  }

  return api.post(
    '/auth/magic-link/request',
    body
  );
};

export const verifyMagicLink = (token) =>
  api.post(
    '/auth/magic-link/verify',
    { token }
  );

export const refreshToken = () =>
  api.post('/auth/refresh');

export const checkEmailExists = (email) =>
  api.post(
    '/auth/check-email',
    { email }
  );

// Personal Invitations (Phase 2 — Feb 2026)
export const createPersonalInvitation = (data) =>
  api.post(
    '/invites/personal',
    data
  );

export const listPersonalInvitations = (teamId) =>
  api.get(
    `/invites/personal/team/${teamId}`
  );

export const resendPersonalInvitation = (id, expectedEmail) =>
  api.post(
    `/invites/personal/${id}/resend`, {},
    { params: expectedEmail === undefined ? {} : { expected_email: expectedEmail } }
  );

export const cancelPersonalInvitation = (id) =>
  api.delete(
    `/invites/personal/${id}`
  );

// User Context - SPRINT V3.3
export const getUserContext = () =>
  api.get('/auth/me/context');

// Team Code Validation & Registration
export const registerWithCode = (data) =>
  api.post(
    '/auth/register-with-code',
    data
  );

// Registration V2 - New endpoints
export const validateRegistrationCode = (code) =>
  api.post(
    '/register/validate-code',
    { code }
  );

export const registerPlayer = (data) =>
  api.post(
    '/register/player',
    data
  );

export const registerCoach = (data) =>
  api.post(
    '/register/coach',
    data
  );

export const registerParent = (data) =>
  api.post(
    '/register/parent',
    data
  );

export const getTeamCodes = (teamId) =>
  api.get(
    `/register/teams/${teamId}/codes`
  );

export const regenerateTeamCodes = (
  teamId,
  codeType = 'all'
) =>
  api.post(
    `/register/teams/${teamId}/codes/regenerate?code_type=${codeType}`
  );

export const getLokSummary = (teamId) =>
  api.get(
    `/register/teams/${teamId}/lok-summary`
  );

// Team timezone
export const getTeamTimezone = (teamId) =>
  api.get(
    `/teams/${teamId}/timezone`
  );

export const setTeamTimezone = (
  teamId,
  timezone
) =>
  api.put(
    `/teams/${teamId}/timezone`,
    { timezone }
  );

// Roster
export const getTeamRoster = (teamId) =>
  api.get(
    `/teams/${teamId}/roster`
  );

export const createRosterPlayer = (
  teamId,
  data
) =>
  api.post(
    `/teams/${teamId}/roster`,
    data
  );

export const updateRosterPlayer = (
  playerId,
  data
) =>
  api.patch(
    `/roster/${playerId}`,
    data
  );

export const deleteRosterPlayer = (playerId) =>
  api.delete(
    `/roster/${playerId}`
  );

// Roster-first onboarding actions
export const createRosterActivationLink = (
  teamId,
  playerProfileId,
  data = {}
) =>
  api.post(
    `/teams/${teamId}/roster/${playerProfileId}/activation-link`,
    data
  );

export const bulkActivatePlayers = (
  teamId,
  players
) =>
  api.post(
    `/teams/${teamId}/roster/activation-links/bulk`,
    { players }
  );

export const suggestPlayerUsername = (
  teamId,
  playerProfileId
) =>
  api.get(
    `/teams/${teamId}/roster/${playerProfileId}/suggest-username`
  );

export const sendPlayerResetLink = (
  teamId,
  playerProfileId,
  expectedEmail
) =>
  api.post(
    `/teams/${teamId}/roster/${playerProfileId}/send-reset-link`,
    {},
    { params: expectedEmail === undefined ? {} : { expected_email: expectedEmail } }
  );

export const inviteParentForPlayer = (
  teamId,
  playerProfileId,
  data
) =>
  api.post(
    `/teams/${teamId}/roster/${playerProfileId}/invite-parent`,
    data
  );

export const linkRosterAccount = (
  teamId,
  playerProfileId,
  data
) =>
  api.post(
    `/teams/${teamId}/roster/${playerProfileId}/link-account`,
    data
  );

export const requestVerifyLoginEmail = (
  teamId,
  playerProfileId
) =>
  api.post(
    `/teams/${teamId}/roster/${playerProfileId}/verify-login-email`,
    {
      return_origin:
        window.location.origin,
    }
  );

export const confirmVerifyEmail = (token) =>
  api.post(
    '/auth/verify-email/confirm',
    { token }
  );

// Roster-first bulk import wizard
export const previewRosterImport = (
  teamId,
  data
) =>
  api.post(
    `/teams/${teamId}/roster/import/preview`,
    data
  );

export const commitRosterImport = (
  teamId,
  data
) =>
  api.post(
    `/teams/${teamId}/roster/import/commit`,
    data
  );

// Guardian Links
export const getGuardianLinks = (teamId) =>
  api.get(
    `/teams/${teamId}/guardian-links`
  );

export const createGuardianLink = (
  teamId,
  data
) =>
  api.post(
    `/teams/${teamId}/guardian-links`,
    data
  );

export const deleteGuardianLink = (linkId) =>
  api.delete(
    `/guardian-links/${linkId}`
  );

export const getGuardianLinksByParent = (
  parentUserId
) =>
  api.get(
    `/guardian-links/by-parent/${parentUserId}`
  );

export const getGuardianLinksByPlayer = (
  playerProfileId
) =>
  api.get(
    `/guardian-links/by-player/${playerProfileId}`
  );

// Exercise Curation
export const suggestExercise = (exerciseId) =>
  api.post(
    `/exercises/${exerciseId}/suggest`
  );

export const promoteExerciseToOrg = (exerciseId) =>
  api.post(
    `/exercises/${exerciseId}/promote-org`
  );

export const promoteExerciseToGlobal = (exerciseId) =>
  api.post(
    `/exercises/${exerciseId}/promote-global`
  );

export const getTeamParents = (teamId) =>
  api.get(
    `/teams/${teamId}/parents`
  );

export const getPlayersForLinking = (teamId) =>
  api.get(
    `/teams/${teamId}/players-for-linking`
  );

// Guardian Contacts
export const getGuardianContacts = (
  teamId,
  { includeDismissed = false } = {}
) =>
  api.get(
    `/teams/${teamId}/guardian-contacts`,
    {
      params: {
        include_dismissed:
          includeDismissed,
      },
    }
  );

export const patchGuardianContact = (
  teamId,
  contactId,
  data
) =>
  api.patch(
    `/teams/${teamId}/guardian-contacts/${contactId}`,
    data
  );

export const dismissGuardianContact = (
  teamId,
  contactId
) =>
  api.post(
    `/teams/${teamId}/guardian-contacts/${contactId}/dismiss`
  );

export const inviteGuardianContacts = (
  teamId,
  contactIds
) =>
  api.post(
    `/teams/${teamId}/guardian-contacts/invite`,
    {
      contact_ids: contactIds,
    }
  );

// Memberships
export const updateMemberRole = (
  membershipId,
  role
) =>
  api.patch(
    `/memberships/${membershipId}`,
    { role }
  );

export const removeMember = (
  membershipId
) =>
  api.delete(
    `/memberships/${membershipId}`
  );

// Self-service team onboarding + billing
export const provisionSelfServiceTeam = (data) =>
  api.post(
    '/onboarding/team',
    data
  );

// Account subscription
export const createAccountCheckout = (originUrl) =>
  api.post(
    '/billing/checkout',
    {
      origin_url: originUrl,
    }
  );

export const getAccountBillingStatus = () =>
  api.get(
    '/billing/status'
  );

export const createAccountBillingPortal = (returnUrl) =>
  api.post(
    '/billing/portal',
    {
      return_url: returnUrl,
    }
  );

// Legacy team-scoped billing
export const createTeamCheckout = (
  teamId,
  originUrl,
  promotionCode
) =>
  api.post(
    `/billing/teams/${teamId}/checkout`,
    {
      origin_url: originUrl,
      ...(promotionCode !== undefined ? { promotion_code: promotionCode } : {}),
    }
  );

export const getTeamBillingStatus = (
  teamId
) =>
  api.get(
    `/billing/teams/${teamId}/status`
  );

export const createTeamBillingPortal = (
  teamId,
  returnUrl
) =>
  api.post(
    `/billing/teams/${teamId}/portal`,
    {
      return_url: returnUrl,
    }
  );

// Invites
export const getTeamInvites = (teamId) =>
  api.get(
    `/teams/${teamId}/invites`
  );

export const provisionTeamInvites = (teamId) =>
  api.post(
    `/teams/${teamId}/invites/provision`
  );

export const regenerateInviteCode = (
  teamId,
  role
) =>
  api.post(
    `/teams/${teamId}/invites/regenerate`,
    { role }
  );

export const getPublicInvite = (code) =>
  api.get(
    `/invites/${code}`
  );

export const getRosterForLinking = (teamId) =>
  api.get(
    `/teams/${teamId}/roster-for-linking`
  );

// Social sign-in (Emergent-managed Google; Apple reuses the same endpoints)
export const socialGoogleAuth = (sessionId) =>
  api.post('/auth/social/google', { session_id: sessionId });
export const socialGoogleLink = (sessionId) =>
  api.post('/auth/social/google/link', { session_id: sessionId });
export const socialGoogleRegister = (pendingToken) =>
  api.post('/auth/social/google/register', { pending_token: pendingToken });
export const socialGoogleAttach = (pendingToken) =>
  api.post('/auth/social/google/attach', { pending_token: pendingToken });
export const socialLoginMethods = () => api.get('/auth/social/methods');
export const socialGoogleUnlink = (data = {}) =>
  api.delete('/auth/social/google', { data });

// Join Flow
export const joinTeamWithCode = (code, data) =>
  api.post(`/join/${encodeURIComponent(code)}/account`, data);
export const joinAsPlayer = (
  code,
  data
) =>
  api.post(
    `/join/${code}/player`,
    data
  );

export const joinAsParent = (
  code,
  data
) =>
  api.post(
    `/join/${code}/parent`,
    data
  );

export const joinAsCoach = (
  code,
  data
) =>
  api.post(
    `/join/${code}/coach`,
    data
  );

// Password Reset
export const requestPasswordReset = (email) =>
  api.post(
    '/auth/forgot-password',
    { email }
  );

export const requestSelfVerifyLoginEmail = (
  candidateEmail
) =>
  api.post(
    '/auth/me/verify-login-email',
    {
      candidate_email: candidateEmail,
      return_origin:
        window.location.origin,
    }
  );

export const verifyResetToken = (token) =>
  api.get(
    `/auth/reset-password/verify?token=${token}`
  );

export const resetPassword = (
  token,
  newPassword
) =>
  api.post(
    '/auth/reset-password',
    {
      token,
      new_password: newPassword,
    }
  );

// Teams
export const getAllTeams = () =>
  api.get('/teams');

export const createTeam = (data) =>
  api.post(
    '/teams',
    data
  );

export const getTeam = (teamId) =>
  api.get(
    `/teams/${teamId}`
  );

export const deleteTeam = (teamId) =>
  api.delete(
    `/teams/${teamId}`
  );

export const archiveTeam = (teamId) =>
  api.post(
    `/teams/${teamId}/archive`
  );

export const restoreTeam = (teamId) =>
  api.post(
    `/teams/${teamId}/restore`
  );

/**
 * @deprecated Use getTeamRoster() for player lists instead.
 * This returns user accounts, not player profiles.
 * See DATA_ARCHITECTURE.md for details.
 */
export const getTeamMembers = (teamId) =>
  api.get(
    `/teams/${teamId}/members`
  );

export const addTeamMember = (
  teamId,
  userId
) =>
  api.put(
    `/teams/${teamId}/add-member/${userId}`
  );

export const addEventType = (
  teamId,
  data
) =>
  api.post(
    `/teams/${teamId}/event-types`,
    data
  );

export const updateUser = (
  userId,
  data
) =>
  api.put(
    `/users/${userId}`,
    data
  );

// Roles
export const getAvailableRoles = () =>
  api.get('/roles');

export const getTeamMembersWithRoles = (teamId) =>
  api.get(
    `/teams/${teamId}/members/roles`
  );

export const changeMemberRole = (
  teamId,
  userId,
  newRole
) =>
  api.put(
    `/teams/${teamId}/members/${userId}/role`,
    {
      user_id: userId,
      team_id: teamId,
      new_role: newRole,
    }
  );

export const getUserRoles = (userId) =>
  api.get(
    `/users/${userId}/roles`
  );

// Safe role transitions
export const makePlayerIntoLeader = (
  teamId,
  playerProfileId,
  payload
) =>
  api.post(
    `/teams/${teamId}/players/${playerProfileId}/make-leader`,
    payload
  );

export const makeMemberIntoPlayer = (
  teamId,
  userId,
  payload
) =>
  api.post(
    `/teams/${teamId}/members/${userId}/make-player`,
    payload
  );

export const linkPlayerProfile = (
  teamId,
  userId,
  playerProfileId
) =>
  api.post(
    `/teams/${teamId}/members/${userId}/link-player-profile`,
    {
      player_profile_id:
        playerProfileId,
    }
  );

export const grantLeaderRole = (
  teamId,
  userId,
  leaderRole
) =>
  api.post(
    `/teams/${teamId}/members/${userId}/grant-leader-role`,
    {
      leader_role: leaderRole,
    }
  );

export const deactivateMembership = (
  teamId,
  membershipId,
  reason
) =>
  api.post(
    `/teams/${teamId}/memberships/${membershipId}/deactivate`,
    {
      reason: reason || null,
    }
  );

// P1C — Team transfer
export const transferOrgMemberTeam = (
  orgId,
  userId,
  payload
) =>
  api.post(
    `/organizations/${orgId}/members/${userId}/team-transfer`,
    payload
  );

// P2 — Audit log viewer
export const getAuditLog = (
  params = {}
) =>
  api.get(
    '/audit-log',
    { params }
  );

// Guardian links
export const getTeamGuardianLinks = (teamId) =>
  api.get(
    `/teams/${teamId}/guardian-links`
  );

// Dashboard
export const getDashboardLayout = (userId) =>
  api.get(
    `/users/${userId}/dashboard`
  );

export const updateDashboardLayout = (
  userId,
  widgets
) =>
  api.put(
    `/users/${userId}/dashboard`,
    { widgets }
  );

// Organizations
export const getMyOrganizations = () =>
  api.get(
    '/organizations/my'
  );

export const getOrganization = (orgId) =>
  api.get(
    `/organizations/${orgId}`
  );

export const getOrganizationOverview = (orgId) =>
  api.get(
    `/organizations/${orgId}/overview`
  );

export const getOrganizationTeams = (orgId) =>
  api.get(
    `/organizations/${orgId}/teams`
  );

export const getOrganizationMembers = (orgId) =>
  api.get(
    `/organizations/${orgId}/members`
  );

export const getAllOrganizationMembers = (orgId) =>
  api.get(
    `/organizations/${orgId}/all-members`
  );

export const getOrganizationRoles = (orgId) =>
  api.get(
    `/organizations/${orgId}/roles`
  );

export const getOrgRoleCandidates = (orgId) =>
  api.get(
    `/organizations/${orgId}/role-candidates`
  );

export const getOrganizationStats = (orgId) =>
  api.get(
    `/organizations/${orgId}/stats`
  );

export const createOrganizationTeam = (
  orgId,
  data
) =>
  api.post(
    `/organizations/${orgId}/teams`,
    data
  );

export const addOrganizationMember = (
  orgId,
  data
) =>
  api.post(
    `/organizations/${orgId}/members`,
    data
  );

export const updateOrganizationMember = (
  orgId,
  userId,
  role
) =>
  api.put(
    `/organizations/${orgId}/members/${userId}?role=${role}`
  );

export const removeOrganizationMember = (
  orgId,
  userId
) =>
  api.delete(
    `/organizations/${orgId}/members/${userId}`
  );

// Admin Organization Management
export const deleteOrganization = (
  orgId,
  confirmName
) =>
  api.delete(
    `/admin/organizations/${orgId}?confirm_name=${encodeURIComponent(confirmName)}`
  );

export const archiveOrganization = (orgId) =>
  api.post(
    `/admin/organizations/${orgId}/archive`
  );

export const restoreOrganization = (orgId) =>
  api.post(
    `/admin/organizations/${orgId}/restore`
  );

export const pauseOrganization = (orgId) =>
  api.post(
    `/admin/organizations/${orgId}/pause`
  );

export const activateOrganization = (
  orgId,
  plan = 'pro'
) =>
  api.post(
    `/admin/organizations/${orgId}/activate?subscription_plan=${plan}`
  );

// Locations
export const getLocations = (teamId) =>
  api.get(
    `/teams/${teamId}/locations`
  );

export const createLocation = (
  teamId,
  data
) =>
  api.post(
    `/teams/${teamId}/locations`,
    data
  );

export const deleteLocation = (locationId) =>
  api.delete(
    `/locations/${locationId}`
  );

// Events
export const getEvents = (teamId) =>
  api.get(
    `/teams/${teamId}/events`
  );

export const getAllEvents = () =>
  api.get(
    '/events/all'
  );

export const createEvent = (
  teamId,
  data
) =>
  api.post(
    `/teams/${teamId}/events`,
    data
  );

export const getEvent = (eventId) =>
  api.get(
    `/events/${eventId}`
  );

export const updateEvent = (
  eventId,
  data
) =>
  api.put(
    `/events/${eventId}`,
    data
  );

export const copyEvent = (
  eventId,
  data
) =>
  api.post(
    `/events/${eventId}/copy`,
    data
  );

export const deleteEvent = (eventId) =>
  api.delete(
    `/events/${eventId}`
  );

// RSVPs
export const getRsvps = (eventId) =>
  api.get(
    `/events/${eventId}/rsvps`
  );

export const getRsvpSummary = (eventId) =>
  api.get(
    `/events/${eventId}/rsvp-summary`
  );

export const createRsvp = (
  eventId,
  status,
  onBehalfOfUserId = null
) =>
  api.post(
    `/events/${eventId}/rsvp`,
    onBehalfOfUserId
      ? {
          status,
          on_behalf_of_user_id:
            onBehalfOfUserId,
        }
      : {
          status,
        }
  );

export const getEventMyChildren = (eventId) =>
  api.get(
    `/events/${eventId}/my-children`
  );

// Invitations/Kallelser
export const sendInvitation = (
  eventId,
  data
) =>
  api.post(
    `/events/${eventId}/send-invitation`,
    data
  );

export const sendReminder = (eventId) =>
  api.post(
    `/events/${eventId}/send-reminder`
  );

export const getEventInvitations = (eventId) =>
  api.get(
    `/events/${eventId}/invitations`
  );

// Team Groups
export const getTeamGroups = (teamId) =>
  api.get(
    `/teams/${teamId}/groups`
  );

export const createTeamGroup = (
  teamId,
  data
) =>
  api.post(
    `/teams/${teamId}/groups`,
    data
  );

export const updateTeamGroup = (
  teamId,
  groupId,
  data
) =>
  api.put(
    `/teams/${teamId}/groups/${groupId}`,
    data
  );

export const deleteTeamGroup = (
  teamId,
  groupId
) =>
  api.delete(
    `/teams/${teamId}/groups/${groupId}`
  );

// Training Sessions
export const getTrainingSessions = (teamId) =>
  api.get(
    `/teams/${teamId}/training-sessions`
  );

export const createTrainingSession = (
  teamId,
  data
) =>
  api.post(
    `/teams/${teamId}/training-sessions`,
    data
  );

export const getTrainingSession = (sessionId) =>
  api.get(
    `/training-sessions/${sessionId}`
  );

export const updateTrainingSession = (
  sessionId,
  data
) =>
  api.put(
    `/training-sessions/${sessionId}`,
    data
  );

export const deleteTrainingSession = (sessionId) =>
  api.delete(
    `/training-sessions/${sessionId}`
  );

// Training Plans
export const getTrainingPlans = (
  teamId,
  status = null
) => {
  const params =
    status
      ? `?status=${status}`
      : '';

  return api.get(
    `/teams/${teamId}/training-plans${params}`
  );
};

export const createTrainingPlan = (
  teamId,
  data
) =>
  api.post(
    `/teams/${teamId}/training-plans`,
    data
  );

export const getTrainingPlan = (planId) =>
  api.get(
    `/training-plans/${planId}`
  );

export const updateTrainingPlan = (
  planId,
  data
) =>
  api.put(
    `/training-plans/${planId}`,
    data
  );

export const deleteTrainingPlan = (planId) =>
  api.delete(
    `/training-plans/${planId}`
  );

export const publishTrainingPlan = (
  planId,
  repeatData = null
) => {
  if (repeatData) {
    return api.post(
      `/training-plans/${planId}/publish`,
      repeatData
    );
  }

  return api.post(
    `/training-plans/${planId}/publish`
  );
};

export const unpublishTrainingPlan = (planId) =>
  api.post(
    `/training-plans/${planId}/unpublish`
  );

export const getTrainingPlansStatusSummary = (teamId) =>
  api.get(
    `/teams/${teamId}/training-plans/status-summary`
  );

export const getTrainingPlansPublishPreview = (
  teamId,
  horizon = 'week'
) =>
  api.get(
    `/teams/${teamId}/training-plans/publish-preview?horizon=${horizon}`
  );

export const bulkPublishTrainingPlans = (
  teamId,
  planIds
) =>
  api.post(
    `/teams/${teamId}/training-plans/bulk-publish`,
    {
      plan_ids: planIds,
    }
  );

export const bulkDeleteTrainingPlans = (
  planIds
) =>
  api.post(
    '/training/plans-bulk-delete',
    {
      plan_ids: planIds,
    }
  );

export const cancelTrainingPlan = (planId) =>
  api.post(
    `/training/plan/${planId}/cancel`
  );

export const duplicateTrainingPlan = (
  planId,
  data
) =>
  api.post(
    `/training/plan/${planId}/duplicate`,
    data
  );

export const publishTrainingPlansWeek = (teamId) =>
  api.post(
    `/teams/${teamId}/training-plans/publish-week`
  );

export const publishTrainingPlansMonth = (teamId) =>
  api.post(
    `/teams/${teamId}/training-plans/publish-month`
  );

export const getTrainingPlanSeriesEvents = (planId) =>
  api.get(
    `/training-plans/${planId}/series-events`
  );

export const deleteTrainingPlanEvents = (
  planId,
  eventIds
) =>
  api.post(
    `/training-plans/${planId}/delete-events`,
    {
      event_ids: eventIds,
    }
  );

export const getPublishedTrainingPlans = (
  teamId,
  startDate = null,
  endDate = null
) => {
  const params = new URLSearchParams();

  if (startDate) {
    params.append(
      'start_date',
      startDate
    );
  }

  if (endDate) {
    params.append(
      'end_date',
      endDate
    );
  }

  const queryString =
    params.toString()
      ? `?${params.toString()}`
      : '';

  return api.get(
    `/teams/${teamId}/published-training-plans${queryString}`
  );
};

// Get all training EVENTS for Training Calendar
export const getTrainingEvents = (
  teamId,
  startDate = null,
  endDate = null
) => {
  const params = new URLSearchParams();

  if (startDate) {
    params.append(
      'start_date',
      startDate
    );
  }

  if (endDate) {
    params.append(
      'end_date',
      endDate
    );
  }

  const queryString =
    params.toString()
      ? `?${params.toString()}`
      : '';

  return api.get(
    `/teams/${teamId}/training-events${queryString}`
  );
};

// Planer
export const getPlanMeta = () =>
  api.get(
    '/plans/options/meta'
  );

export const getPlanMethodologyMeta = (
  lang = 'sv'
) =>
  api.get(
    `/plans/methodology/meta?lang=${lang}`
  );

// Plan Bank
export const getPlanBank = (
  sport = 'football',
  timeHorizon = null
) =>
  api.get(
    `/plan-bank?sport=${sport}${
      timeHorizon
        ? `&time_horizon=${timeHorizon}`
        : ''
    }`
  );

export const getPlanTemplate = (templateId) =>
  api.get(
    `/plan-bank/${templateId}`
  );

export const createPlanFromTemplate = (
  templateId,
  payload
) =>
  api.post(
    `/plan-bank/${templateId}/use`,
    payload
  );

export const resolvePlan = (data) =>
  api.post(
    '/plans/resolve',
    data
  );

export const getPlans = (teamId) =>
  api.get(
    `/plans/teams/${teamId}`
  );

export const createPlan = (
  teamId,
  data
) =>
  api.post(
    `/plans/teams/${teamId}`,
    data
  );

export const getPlan = (planId) =>
  api.get(
    `/plans/${planId}`
  );

export const updatePlan = (
  planId,
  data
) =>
  api.put(
    `/plans/${planId}`,
    data
  );

export const deletePlan = (
  planId,
  mode = 'drafts'
) =>
  api.delete(
    `/plans/${planId}?mode=${mode}`
  );

export const getPlanDeleteSummary = (
  planId
) =>
  api.get(
    `/plans/${planId}/delete-summary`
  );

export const duplicatePlan = (planId) =>
  api.post(
    `/plans/${planId}/duplicate`
  );

export const updatePlanWeek = (
  planId,
  weekId,
  data
) =>
  api.put(
    `/plans/${planId}/weeks/${weekId}`,
    data
  );

export const duplicatePlanWeek = (
  planId,
  weekId
) =>
  api.post(
    `/plans/${planId}/weeks/${weekId}/duplicate`
  );

export const updatePlanSession = (
  planId,
  sessionId,
  data
) =>
  api.put(
    `/plans/${planId}/sessions/${sessionId}`,
    data
  );

export const duplicatePlanSession = (
  planId,
  sessionId
) =>
  api.post(
    `/plans/${planId}/sessions/${sessionId}/duplicate`
  );

export const updatePlanBlock = (
  planId,
  periodId,
  data
) =>
  api.put(
    `/plans/${planId}/blocks/${periodId}`,
    data
  );

export const movePlanBlock = (
  planId,
  periodId,
  direction
) =>
  api.post(
    `/plans/${planId}/blocks/${periodId}/move`,
    {
      direction,
    }
  );

export const duplicatePlanBlock = (
  planId,
  periodId
) =>
  api.post(
    `/plans/${planId}/blocks/${periodId}/duplicate`
  );

export const setPlanBlockLength = (
  planId,
  periodId,
  weeks
) =>
  api.post(
    `/plans/${planId}/blocks/${periodId}/length`,
    {
      weeks,
    }
  );

export const repeatPlanBlock = (
  planId,
  periodId,
  complexityLevel = null
) =>
  api.post(
    `/plans/${planId}/blocks/${periodId}/repeat`,
    {
      complexity_level:
        complexityLevel,
    }
  );

export const copyPlanToNextSeason = (planId) =>
  api.post(
    `/plans/${planId}/copy-to-next-season`
  );

export const pushPlanToCalendar = (
  planId,
  config
) =>
  api.post(
    `/plans/${planId}/calendar`,
    config
  );

export const schedulePlanPreview = (
  planId,
  config
) =>
  api.post(
    `/plans/${planId}/schedule/preview`,
    config
  );

export const commitPlanSchedule = (
  planId,
  rows
) =>
  api.post(
    `/plans/${planId}/schedule/commit`,
    {
      rows,
    }
  );

export const publishPlannedSessions = (
  planId
) =>
  api.post(
    `/plans/${planId}/publish-planned`
  );

// Training Plan Blocks
export const addTrainingBlock = (
  planId,
  data
) =>
  api.post(
    `/training-plans/${planId}/blocks`,
    data
  );

export const updateTrainingBlock = (
  planId,
  blockId,
  data
) =>
  api.put(
    `/training-plans/${planId}/blocks/${blockId}`,
    data
  );

export const deleteTrainingBlock = (
  planId,
  blockId
) =>
  api.delete(
    `/training-plans/${planId}/blocks/${blockId}`
  );

// Event Training Plan
export const getEventTrainingPlan = (eventId) =>
  api.get(
    `/events/${eventId}/training-plan`
  );

export const createEventTrainingPlan = (eventId) =>
  api.post(
    `/events/${eventId}/training-plan`
  );

// Match
export const getLineup = (eventId) =>
  api.get(
    `/events/${eventId}/lineup`
  );

export const createLineup = (
  eventId,
  data
) =>
  api.post(
    `/events/${eventId}/lineup`,
    data
  );

export const getMatchResult = (eventId) =>
  api.get(
    `/events/${eventId}/result`
  );

export const createMatchResult = (
  eventId,
  data
) =>
  api.post(
    `/events/${eventId}/result`,
    data
  );

// Matches
export const getMatches = (
  teamId,
  year = null,
  status = null
) => {
  const params =
    new URLSearchParams();

  if (year) {
    params.append(
      'year',
      year
    );
  }

  if (status) {
    params.append(
      'status',
      status
    );
  }

  const queryString =
    params.toString()
      ? `?${params.toString()}`
      : '';

  return api.get(
    `/teams/${teamId}/matches${queryString}`
  );
};

export const getMatch = (matchId) =>
  api.get(
    `/matches/${matchId}`
  );

export const createMatch = (
  teamId,
  data
) =>
  api.post(
    `/teams/${teamId}/matches`,
    data
  );

export const updateMatch = (
  matchId,
  data
) =>
  api.put(
    `/matches/${matchId}`,
    data
  );

export const deleteMatch = (matchId) =>
  api.delete(
    `/matches/${matchId}`
  );

// SvFF
export const getSvffStatus = (teamId) =>
  api.get(
    `/svff/status?team_id=${teamId}`
  );

export const setSvffCredentials = (
  teamId,
  apiKey
) =>
  api.post(
    '/svff/credentials',
    {
      team_id: teamId,
      api_key: apiKey,
    }
  );

export const deleteSvffCredentials = (
  teamId
) =>
  api.delete(
    `/svff/credentials?team_id=${teamId}`
  );

export const getSvffTeamEngagements = (
  teamId
) =>
  api.get(
    `/svff/team-engagements?team_id=${teamId}`
  );

export const previewSvffSeries = (
  payload
) =>
  api.post(
    '/svff/preview-series',
    payload
  );

export const connectSvffSeries = (
  payload
) =>
  api.post(
    '/svff/connect-series',
    payload
  );

export const getSvffMappings = (
  teamId
) =>
  api.get(
    `/svff/mappings?team_id=${teamId}`
  );

export const syncSvffMappingNow = (
  mappingId
) =>
  api.post(
    `/svff/mappings/${mappingId}/sync-now`
  );

export const disconnectSvffMapping = (
  mappingId
) =>
  api.delete(
    `/svff/mappings/${mappingId}`
  );

export const getSvffMappingStandings = (
  mappingId
) =>
  api.get(
    `/svff/mappings/${mappingId}/standings`
  );

export const getSvffMappingSyncLogs = (
  mappingId
) =>
  api.get(
    `/svff/mappings/${mappingId}/sync-logs`
  );

export const getSvffMatchSyncLogs = (
  matchId
) =>
  api.get(
    `/svff/matches/${matchId}/sync-logs`
  );

export const markSvffLogRead = (
  logId
) =>
  api.post(
    `/svff/sync-logs/${logId}/mark-read`
  );

export const resolveSvffResultConflict = (
  conflictId,
  action
) =>
  api.post(
    `/svff/result-conflicts/${conflictId}/resolve`,
    {
      action,
    }
  );

// Match Events
export const getMatchEvents = (matchId) =>
  api.get(
    `/matches/${matchId}/events`
  );

export const createMatchEvent = (
  matchId,
  data
) =>
  api.post(
    `/matches/${matchId}/events`,
    data
  );

export const sendMatchNotification = (
  matchId,
  eventType
) =>
  api.post(
    `/matches/${matchId}/notify`,
    {
      event_type: eventType,
    }
  );

export const deleteMatchEvent = (
  matchId,
  eventId
) =>
  api.delete(
    `/matches/${matchId}/events/${eventId}`
  );

// Match Live
export const getMatchLive = (matchId) =>
  api.get(
    `/matches/${matchId}/live`
  );

// Match Statistics
export const getMatchStatistics = (matchId) =>
  api.get(
    `/matches/${matchId}/statistics`
  );

export const updateMatchStatistics = (
  matchId,
  data
) =>
  api.put(
    `/matches/${matchId}/statistics`,
    data
  );

// Player Match Statistics
export const getPlayerMatchStats = (matchId) =>
  api.get(
    `/matches/${matchId}/player-statistics`
  );

export const updatePlayerMatchStats = (
  matchId,
  playerId,
  data
) =>
  api.put(
    `/matches/${matchId}/player-statistics/${playerId}`,
    data
  );

// Match Analytics
export const getMatchAnalytics = (matchId) =>
  api.get(
    `/matches/${matchId}/analytics`
  );

// Match Lineup
export const getMatchLineup = (matchId) =>
  api.get(
    `/matches/${matchId}/lineup`
  );

export const updateMatchLineup = (
  matchId,
  data
) =>
  api.put(
    `/matches/${matchId}/lineup`,
    data
  );

export const publishMatchLineup = (matchId) =>
  api.post(
    `/matches/${matchId}/lineup/publish`
  );

export const addSubstitutionEvent = (
  matchId,
  data
) =>
  api.post(
    `/matches/${matchId}/lineup/substitution`,
    data
  );

// Match Clock
export const getMatchClock = (matchId) =>
  api.get(
    `/matches/${matchId}/clock`
  );

export const startMatchClock = (matchId) =>
  api.post(
    `/matches/${matchId}/clock/start`
  );

export const halfTimeMatchClock = (matchId) =>
  api.post(
    `/matches/${matchId}/clock/half-time`
  );

export const startSecondHalf = (matchId) =>
  api.post(
    `/matches/${matchId}/clock/start-second-half`
  );

export const finishMatchClock = (matchId) =>
  api.post(
    `/matches/${matchId}/clock/finish`
  );

export const resumeMatchClock = (matchId) =>
  api.post(
    `/matches/${matchId}/clock/resume`
  );

// Match Tactics
export const getMatchTactics = (matchId) =>
  api.get(
    `/matches/${matchId}/tactics`
  );

export const getMatchTacticsFull = (matchId) =>
  api.get(
    `/matches/${matchId}/tactics/full`
  );

export const updateMatchTactics = (
  matchId,
  data
) =>
  api.put(
    `/matches/${matchId}/tactics`,
    data
  );

// Role templates
export const listRoleTemplates = (
  teamId,
  includeArchived = false
) =>
  api.get(
    `/teams/${teamId}/role-templates`,
    {
      params: {
        include_archived:
          includeArchived,
      },
    }
  );

export const createRoleTemplate = (
  teamId,
  data
) =>
  api.post(
    `/teams/${teamId}/role-templates`,
    data
  );

export const updateRoleTemplate = (
  teamId,
  templateId,
  data
) =>
  api.put(
    `/teams/${teamId}/role-templates/${templateId}`,
    data
  );

export const archiveRoleTemplate = (
  teamId,
  templateId
) =>
  api.post(
    `/teams/${teamId}/role-templates/${templateId}/archive`
  );

export const updateMatchTacticInstructions = (
  matchId,
  tacticId,
  data
) =>
  api.put(
    `/matches/${matchId}/tactics/${tacticId}/instructions`,
    data
  );

export const copyTacticsFromLastMatch = (
  matchId
) =>
  api.post(
    `/matches/${matchId}/copy-tactics`
  );

export const addTacticReference = (
  matchId,
  tacticId
) =>
  api.post(
    `/matches/${matchId}/tactics/add-reference`,
    {
      tactic_id: tacticId,
    }
  );

export const removeTacticReference = (
  matchId,
  tacticId
) =>
  api.post(
    `/matches/${matchId}/tactics/remove-reference`,
    {
      tactic_id: tacticId,
    }
  );

// Tactics Library
export const getTactics = (
  params = {}
) => {
  const queryParams =
    new URLSearchParams();

  if (params.team_id) {
    queryParams.append(
      'team_id',
      params.team_id
    );
  }

  if (params.phase) {
    queryParams.append(
      'phase',
      params.phase
    );
  }

  if (params.game_format) {
    queryParams.append(
      'game_format',
      params.game_format
    );
  }

  if (params.level) {
    queryParams.append(
      'level',
      params.level
    );
  }

  if (params.search) {
    queryParams.append(
      'search',
      params.search
    );
  }

  return api.get(
    `/tactics?${queryParams.toString()}`
  );
};

export const getTactic = (
  tacticId
) =>
  api.get(
    `/tactics/${tacticId}`
  );

// Aggregated Player Statistics
export const getAggregatedPlayerStats = (
  teamId,
  matchIds
) =>
  api.get(
    `/teams/${teamId}/player-statistics/aggregated?match_ids=${matchIds.join(',')}`
  );

// Match Import
export const previewMatchImport = (
  teamId,
  rows
) =>
  api.post(
    `/teams/${teamId}/matches/import/preview`,
    {
      rows,
    }
  );

export const executeMatchImport = (
  teamId,
  rows
) =>
  api.post(
    `/teams/${teamId}/matches/import/execute`,
    {
      rows,
    }
  );

// Chat
export const getChatMessages = (
  teamId,
  limit = 50
) =>
  api.get(
    `/teams/${teamId}/chat?limit=${limit}`
  );

export const sendChatMessage = (
  teamId,
  text,
  attachments = []
) =>
  api.post(
    `/teams/${teamId}/chat`,
    {
      text,
      attachments,
    }
  );

export const deleteChatMessage = (
  messageId
) =>
  api.delete(
    `/chat/${messageId}`
  );

// Chat Reactions legacy
export const toggleReaction = (
  messageId,
  emoji
) =>
  api.post(
    `/chat/${messageId}/reactions`,
    {
      emoji,
    }
  );

export const getReactions = (
  messageId
) =>
  api.get(
    `/chat/${messageId}/reactions`
  );

// Assessments
export const getEventAssessments = (
  eventId
) =>
  api.get(
    `/events/${eventId}/assessments`
  );

export const createAssessment = (
  eventId,
  data
) =>
  api.post(
    `/events/${eventId}/assessments`,
    data
  );

export const getPlayerAssessments = (
  playerId,
  teamId
) =>
  api.get(
    `/players/${playerId}/assessments?team_id=${teamId}`
  );

export const getTeamAssessmentsOverview = (
  teamId
) =>
  api.get(
    `/teams/${teamId}/assessments/overview`
  );

// Statistics
export const getTeamStatistics = (
  teamId
) =>
  api.get(
    `/teams/${teamId}/statistics`
  );

export const exportStatisticsCsv = (
  teamId
) =>
  api.get(
    `/teams/${teamId}/statistics/export`,
    {
      responseType: 'blob',
    }
  );

// Media
export const getMedia = (
  teamId
) =>
  api.get(
    `/teams/${teamId}/media`
  );

export const createMedia = (
  teamId,
  data
) =>
  api.post(
    `/teams/${teamId}/media`,
    data
  );

export const deleteMedia = (
  mediaId
) =>
  api.delete(
    `/media/${mediaId}`
  );

// Exercise Media
export const getExerciseMedia = (
  exerciseId
) =>
  api.get(
    `/exercises/${exerciseId}/media`
  );

// Feedback
export const getEventFeedback = (
  eventId
) =>
  api.get(
    `/events/${eventId}/feedback`
  );

export const createFeedback = (
  eventId,
  text
) =>
  api.post(
    `/events/${eventId}/feedback`,
    {
      text,
    }
  );

// Exercise Library
export const getExerciseFolders = (
  teamId
) =>
  api.get(
    `/teams/${teamId}/exercises/folders`
  );

export const createExerciseFolder = (
  teamId,
  data
) =>
  api.post(
    `/teams/${teamId}/exercises/folders`,
    data
  );

export const updateExerciseFolder = (
  folderId,
  data
) =>
  api.put(
    `/exercises/folders/${folderId}`,
    data
  );

export const deleteExerciseFolder = (
  folderId
) =>
  api.delete(
    `/exercises/folders/${folderId}`
  );

export const getExerciseCategories = () =>
  api.get(
    '/exercises/categories'
  );

export const getExercises = (
  teamId,
  folderId = null,
  search = null,
  scope = null,
  filters = {}
) => {
  let params = [];
  if (filters.sort_by) params.push(`sort_by=${encodeURIComponent(filters.sort_by)}`);
  if (filters.favorites_only) params.push('favorites_only=true');


  if (folderId) {
    params.push(
      `folder_id=${folderId}`
    );
  }

  if (search) {
    params.push(
      `search=${encodeURIComponent(search)}`
    );
  }

  if (scope) {
    params.push(
      `scope=${scope}`
    );
  }

  if (filters.game_format) {
    params.push(
      `game_format=${filters.game_format}`
    );
  }

  if (filters.focus_area) {
    params.push(
      `focus_area=${filters.focus_area}`
    );
  }

  if (filters.intensity) {
    params.push(
      `intensity=${filters.intensity}`
    );
  }

  if (filters.play_type) {
    params.push(
      `play_type=${encodeURIComponent(filters.play_type)}`
    );
  }

  if (filters.focus_tag) {
    params.push(
      `focus_tag=${encodeURIComponent(filters.focus_tag)}`
    );
  }

  if (filters.training_type) {
    params.push(
      `training_type=${filters.training_type}`
    );
  }

  if (filters.exercise_collection) {
    params.push(
      `exercise_collection=${encodeURIComponent(filters.exercise_collection)}`
    );
  }

  if (filters.game_phase) {
    params.push(
      `game_phase=${encodeURIComponent(filters.game_phase)}`
    );
  }

  if (filters.play_area) {
    params.push(
      `play_area=${encodeURIComponent(filters.play_area)}`
    );
  }

  return api.get(
    `/teams/${teamId}/exercises${
      params.length
        ? '?' + params.join('&')
        : ''
    }`
  );
};

export const getExercise = (
  exerciseId
) =>
  api.get(
    `/exercises/${exerciseId}`
  );

export const createExercise = (
  teamId,
  data
) =>
  api.post(
    `/teams/${teamId}/exercises`,
    data
  );

export const updateExercise = (
  exerciseId,
  data
) =>
  api.put(
    `/exercises/${exerciseId}`,
    data
  );

export const deleteExercise = (
  exerciseId
) =>
  api.delete(
    `/exercises/${exerciseId}`
  );

export const duplicateExercise = (
  exerciseId,
  teamId
) =>
  api.post(
    `/exercises/${exerciseId}/duplicate${
      teamId
        ? `?team_id=${teamId}`
        : ''
    }`
  );

// Exercise recommendations
export const recommendExercisesForBlock = (
  params = {}
) => {
  const q =
    new URLSearchParams();

  if (params.recommended_types?.length) {
    q.set(
      'recommended_types',
      params.recommended_types.join(',')
    );
  }

  if (params.age_group) {
    q.set(
      'age_group',
      params.age_group
    );
  }

  if (params.game_format) {
    q.set(
      'game_format',
      params.game_format
    );
  }

  if (params.training_block) {
    q.set(
      'training_block',
      params.training_block
    );
  }

  if (params.theme) {
    q.set(
      'theme',
      params.theme
    );
  }

  if (params.team_id) {
    q.set(
      'team_id',
      params.team_id
    );
  }

  const query =
    q.toString();

  return api.get(
    `/exercises/recommend/for-block${
      query
        ? `?${query}`
        : ''
    }`
  );
};

// Exercise Favorites & Recent
export const toggleExerciseFavorite = (
  exerciseId
) =>
  api.post(
    `/exercises/${exerciseId}/favorite`
  );

export const getUserFavoriteExercises = () =>
  api.get(
    '/exercises/user/favorites'
  );

export const getFavoriteExercises = (
  teamId
) =>
  api.get(
    `/teams/${teamId}/exercises/favorites`
  );

export const markExerciseUsed = (
  exerciseId
) =>
  api.post(
    `/exercises/${exerciseId}/use`
  );

export const getRecentExercises = (
  teamId,
  limit = 20
) =>
  api.get(
    `/teams/${teamId}/exercises/recent?limit=${limit}`
  );

// Diagrams
export const getDiagrams = (
  exerciseId = null,
  trainingBlockId = null
) => {
  let params = [];

  if (exerciseId) {
    params.push(
      `exercise_id=${exerciseId}`
    );
  }

  if (trainingBlockId) {
    params.push(
      `training_block_id=${trainingBlockId}`
    );
  }

  return api.get(
    `/diagrams${
      params.length
        ? '?' + params.join('&')
        : ''
    }`
  );
};

export const getDiagram = (
  diagramId
) =>
  api.get(
    `/diagrams/${diagramId}`
  );

export const createDiagram = (
  data
) =>
  api.post(
    '/diagrams',
    data
  );

export const updateDiagram = (
  diagramId,
  data
) =>
  api.put(
    `/diagrams/${diagramId}`,
    data
  );

export const deleteDiagram = (
  diagramId
) =>
  api.delete(
    `/diagrams/${diagramId}`
  );

export const duplicateDiagram = (
  diagramId
) =>
  api.post(
    `/diagrams/${diagramId}/duplicate`
  );

// Animations
export const getAnimations = (
  exerciseId = null,
  trainingBlockId = null,
  diagramId = null
) => {
  let params = [];

  if (exerciseId) {
    params.push(
      `exercise_id=${exerciseId}`
    );
  }

  if (trainingBlockId) {
    params.push(
      `training_block_id=${trainingBlockId}`
    );
  }

  if (diagramId) {
    params.push(
      `diagram_id=${diagramId}`
    );
  }

  return api.get(
    `/animations${
      params.length
        ? '?' + params.join('&')
        : ''
    }`
  );
};

export const getAnimation = (
  animationId
) =>
  api.get(
    `/animations/${animationId}`
  );

export const createAnimation = (
  data
) =>
  api.post(
    '/animations',
    data
  );

export const updateAnimation = (
  animationId,
  data
) =>
  api.put(
    `/animations/${animationId}`,
    data
  );

export const deleteAnimation = (
  animationId
) =>
  api.delete(
    `/animations/${animationId}`
  );

export const createAnimationFromDiagram = (
  diagramId
) =>
  api.post(
    `/animations/from-diagram/${diagramId}`
  );

// Evaluation Templates
export const getEvaluationTemplates = (
  teamId,
  type = null,
  frequency = null
) => {
  const params =
    new URLSearchParams();

  if (type) {
    params.append(
      'type',
      type
    );
  }

  if (frequency) {
    params.append(
      'frequency',
      frequency
    );
  }

  params.append(
    'include_standard',
    'true'
  );

  return api.get(
    `/evaluations/teams/${teamId}/templates?${params}`
  );
};

export const getAllTemplatesWithStandard = (
  teamId
) =>
  api.get(
    `/evaluations/teams/${teamId}/all-templates`
  );

export const createEvaluationTemplate = (
  teamId,
  data
) =>
  api.post(
    `/evaluations/teams/${teamId}/templates`,
    data
  );

export const updateEvaluationTemplate = (
  teamId,
  templateId,
  data
) =>
  api.put(
    `/evaluations/teams/${teamId}/templates/${templateId}`,
    data
  );

export const deleteEvaluationTemplate = (
  teamId,
  templateId
) =>
  api.delete(
    `/evaluations/teams/${teamId}/templates/${templateId}`
  );

// Evaluation Settings
export const getEvaluationSettings = (
  teamId
) =>
  api.get(
    `/evaluations/teams/${teamId}/settings`
  );

export const updateMatchEvaluationSettings = (
  teamId,
  data
) =>
  api.put(
    `/evaluations/teams/${teamId}/settings/match`,
    data
  );

export const updateWeeklyEvaluationSettings = (
  teamId,
  data
) =>
  api.put(
    `/evaluations/teams/${teamId}/settings/weekly`,
    data
  );

export const updateDailyEvaluationSettings = (
  teamId,
  data
) =>
  api.put(
    `/evaluations/teams/${teamId}/settings/daily`,
    data
  );

export const updateMonthlyEvaluationSettings = (
  teamId,
  data
) =>
  api.put(
    `/evaluations/teams/${teamId}/settings/monthly`,
    data
  );

export const updateMentalSocialEvaluationSettings = (
  teamId,
  data
) =>
  api.put(
    `/evaluations/teams/${teamId}/settings/mental-social`,
    data
  );

// Send Evaluations
export const sendPostMatchEvaluation = (
  eventId
) =>
  api.post(
    `/events/${eventId}/send-evaluation`
  );

export const sendWeeklyEvaluation = (
  teamId
) =>
  api.post(
    `/evaluations/teams/${teamId}/send-weekly-evaluation`
  );

export const sendDailyEvaluation = (
  teamId
) =>
  api.post(
    `/evaluations/teams/${teamId}/send-daily-evaluation`
  );

export const sendMonthlyEvaluation = (
  teamId
) =>
  api.post(
    `/evaluations/teams/${teamId}/send-monthly-evaluation`
  );

export const sendEvaluationReminder = (
  teamId,
  groupId
) =>
  api.post(
    `/evaluations/teams/${teamId}/sent/${groupId}/reminder`
  );

// My Evaluations
export const getMyEvaluations = (
  teamId,
  status = null
) => {
  const params =
    new URLSearchParams({
      team_id: teamId,
    });

  if (status) {
    params.append(
      'status',
      status
    );
  }

  return api.get(
    `/evaluations/my-evaluations?${params.toString()}`
  );
};

export const getEvaluation = (
  evaluationId
) =>
  api.get(
    `/evaluations/${evaluationId}`
  );

export const submitEvaluation = (
  evaluationId,
  responses,
  templateRevision
) =>
  api.post(
    `/evaluations/${evaluationId}/submit`,
    {
      responses,
      ...(templateRevision ? { template_revision: templateRevision } : {}),
    }
  );

// Evaluation Results
export const getEvaluationResults = (
  teamId,
  eventId = null
) => {
  const params =
    eventId
      ? `?event_id=${eventId}`
      : '';

  return api.get(
    `/evaluations/teams/${teamId}/results${params}`
  );
};

// Evaluation Sent Status
export const getSentEvaluations = (
  teamId
) =>
  api.get(
    `/evaluations/teams/${teamId}/sent`
  );

export const sendLatestMatchEvaluation = (
  teamId
) =>
  api.post(
    `/evaluations/teams/${teamId}/send-latest-match-evaluation`
  );

export const sendEvaluationByTemplate = (
  teamId,
  templateId,
  eventId = null,
  context = {}
) => {
  const params =
    new URLSearchParams({
      template_id: templateId,
    });

  if (eventId) {
    params.append(
      'event_id',
      eventId
    );
  }

  for (const field of ['week_key', 'month_key', 'period_start', 'period_end', 'observation_date']) {
    if (context[field]) params.append(field, context[field]);
  }

  return api.post(
    `/evaluations/teams/${teamId}/send-evaluation?${params}`
  );
};

// Detailed Results
export const getSentEvaluationDetails = (
  teamId,
  batchId
) =>
  api.get(
    `/evaluations/teams/${teamId}/sent/${batchId}/details`
  );

export const getLeaderReport = (
  teamId,
  batchId
) =>
  api.get(
    `/evaluations/teams/${teamId}/sent/${batchId}/report`
  );

export const getTeamWarnings = (
  teamId,
  status = 'pending'
) =>
  api.get(
    `/teams/${teamId}/warnings?status=${status}`
  );

export const markWarningHandled = (
  teamId,
  warningId
) =>
  api.post(
    `/teams/${teamId}/warnings/${warningId}/handle`
  );

export const getEvaluationStats = (
  teamId
) =>
  api.get(
    `/evaluations/teams/${teamId}/stats`
  );

export const getPlayerEvaluationHistory = (
  playerId,
  weeks = 6,
  teamId
) =>
  api.get(
    `/evaluations/players/${playerId}/evaluation-history`,
    { params: { weeks, ...(teamId ? { team_id: teamId } : {}) } }
  );

export const getTeamPlayersStatus = (
  teamId,
  period = '30d'
) =>
  api.get(
    `/evaluations/teams/${teamId}/players-status?period=${period}`
  );

export const getTeamTrends = (
  teamId,
  period = '30d'
) =>
  api.get(
    `/evaluations/teams/${teamId}/team-trends?period=${period}`
  );

export const getPlayerStatus = (
  playerId,
  period = '30d',
  teamId
) =>
  api.get(
    `/evaluations/players/${playerId}/status`,
    { params: { period, ...(teamId ? { team_id: teamId } : {}) } }
  );

// Leaders Chat
export const getLeadersChat = (
  limit = 50,
  teamFilter = null
) => {
  let params =
    `?limit=${limit}`;

  if (teamFilter) {
    params +=
      `&team_filter=${teamFilter}`;
  }

  return api.get(
    `/chat/leaders${params}`
  );
};

export const sendLeadersMessage = (
  text,
  attachments = []
) =>
  api.post(
    '/chat/leaders',
    {
      text,
      attachments,
    }
  );

export const getTeams = () =>
  api.get('/teams');

export const getTeamById = (
  teamId
) =>
  api.get(
    `/teams/${teamId}`
  );

// Push Notifications
export const getVapidKey = () =>
  api.get(
    '/push/vapid-key'
  );

export const subscribePush = (
  subscription
) =>
  api.post(
    '/push/subscribe',
    subscription
  );

export const unsubscribePush = () =>
  api.delete(
    '/push/unsubscribe'
  );

export const getNotificationPreferences = () =>
  api.get(
    '/push/preferences'
  );

export const updateNotificationPreferences = (
  data
) =>
  api.put(
    '/push/preferences',
    data
  );

export const sendTestNotification = () =>
  api.post(
    '/push/send-test'
  );

export const getPushDebugStatus = () =>
  api.get(
    '/notifications/debug/subscription-status'
  );

export const notifyTeam = (
  teamId,
  title,
  body,
  url = '/',
  notificationType = null
) =>
  api.post(
    `/push/notify-team/${teamId}?title=${encodeURIComponent(title)}&body=${encodeURIComponent(body)}&url=${encodeURIComponent(url)}${
      notificationType
        ? `&notification_type=${notificationType}`
        : ''
    }`
  );

// CSV Import
export const previewImport = (
  teamId,
  csvContent
) =>
  api.post(
    `/teams/${teamId}/import/preview`,
    csvContent,
    {
      headers: {
        'Content-Type': 'text/csv',
      },
    }
  );

export const executeImport = (
  teamId,
  data
) =>
  api.post(
    `/teams/${teamId}/import/execute`,
    data
  );

export const getImportTemplate = (
  teamId
) =>
  api.get(
    `/teams/${teamId}/import/template`,
    {
      responseType: 'blob',
    }
  );

// Trupp Preferences
export const getTruppPreferences = (
  userId,
  teamId
) =>
  api.get(
    `/users/${userId}/trupp-preferences/${teamId}`
  );

export const updateTruppPreferences = (
  userId,
  teamId,
  prefs
) =>
  api.put(
    `/users/${userId}/trupp-preferences/${teamId}`,
    prefs
  );

export const resetTruppPreferences = (
  userId,
  teamId
) =>
  api.delete(
    `/users/${userId}/trupp-preferences/${teamId}`
  );

// Block Templates
export const getBlockTemplates = (
  teamId
) =>
  api.get(
    `/teams/${teamId}/block-templates`
  );

export const createBlockTemplate = (
  teamId,
  data
) =>
  api.post(
    `/teams/${teamId}/block-templates`,
    data
  );

export const deleteBlockTemplate = (
  teamId,
  templateId
) =>
  api.delete(
    `/teams/${teamId}/block-templates/${templateId}`
  );

// Gap Analysis
export const getCoachAssessmentTemplates = (
  teamId = null
) => {
  const params =
    teamId
      ? `?team_id=${teamId}`
      : '';

  return api.get(
    `/gap-analysis/templates/coach${params}`
  );
};

export const getMatchingKeys = (
  activityType,
  teamId = null
) => {
  const params =
    teamId
      ? `?team_id=${teamId}`
      : '';

  return api.get(
    `/gap-analysis/matching-keys/${activityType}${params}`
  );
};

export const createDetailedCoachAssessment = (
  teamId,
  eventId,
  data
) =>
  api.post(
    `/gap-analysis/teams/${teamId}/events/${eventId}/coach-assessment`,
    data
  );

export const getEventCoachAssessments = (
  teamId,
  eventId
) =>
  api.get(
    `/gap-analysis/teams/${teamId}/events/${eventId}/coach-assessments`
  );

// Detailed Assessment APIs
export const getEventGapAnalysis = (
  teamId,
  eventId,
  playerId = null
) => {
  const params =
    playerId
      ? `?player_id=${playerId}`
      : '';

  return api.get(
    `/gap-analysis/teams/${teamId}/events/${eventId}/gap-analysis${params}`
  );
};

export const getPlayerGapHistory = (
  teamId,
  playerId,
  weeks = 8
) =>
  api.get(
    `/gap-analysis/teams/${teamId}/players/${playerId}/gap-history?weeks=${weeks}`
  );

// Assessment Settings
export const getTeamAssessmentSettings = (
  teamId
) =>
  api.get(
    `/gap-analysis/teams/${teamId}/assessment-settings`
  );

export const updateTeamAssessmentSettings = (
  teamId,
  assessmentMode
) =>
  api.put(
    `/gap-analysis/teams/${teamId}/assessment-settings`,
    {
      assessment_mode:
        assessmentMode,
    }
  );

// Weekly Assessments
export const createWeeklyCoachAssessment = (
  teamId,
  data
) =>
  api.post(
    `/gap-analysis/teams/${teamId}/weekly-assessment`,
    data
  );

export const getTeamWeeklyAssessments = (
  teamId,
  weekNumber = null
) => {
  const params =
    weekNumber
      ? `?week_number=${weekNumber}`
      : '';

  return api.get(
    `/gap-analysis/teams/${teamId}/weekly-assessments${params}`
  );
};

export const getWeeklyGapAnalysis = (
  teamId,
  weekNumber = null
) => {
  const params =
    weekNumber
      ? `?week_number=${weekNumber}`
      : '';

  return api.get(
    `/gap-analysis/teams/${teamId}/weekly-gap-analysis${params}`
  );
};

// Team Overview & Multi-Coach Comparison
export const getTeamGapOverview = (
  teamId,
  activityType = 'all',
  sortBy = 'gap_desc'
) =>
  api.get(
    `/gap-analysis/teams/${teamId}/team-overview?activity_type=${activityType}&sort_by=${sortBy}`
  );

export const getMultiCoachComparison = (
  teamId,
  playerId = null
) => {
  const params =
    playerId
      ? `?player_id=${playerId}`
      : '';

  return api.get(
    `/gap-analysis/teams/${teamId}/multi-coach-comparison${params}`
  );
};

// Coach-Only Comparison
export const getCoachOnlyComparison = (
  teamId,
  activityType = 'all',
  timePeriod = 'month',
  sortBy = 'spread_desc'
) =>
  api.get(
    `/gap-analysis/teams/${teamId}/coach-comparison?activity_type=${activityType}&time_period=${timePeriod}&sort_by=${sortBy}`
  );

// Player Gap Overview
export const getPlayerGapOverview = (
  teamId,
  activityType = 'all',
  timePeriod = 'month',
  sortBy = 'gap_desc'
) =>
  api.get(
    `/gap-analysis/teams/${teamId}/player-gap-overview?activity_type=${activityType}&time_period=${timePeriod}&sort_by=${sortBy}`
  );

// Praise
export const getUnreadPraiseCount = () =>
  api.get(
    '/praise/unread-count'
  );

export const getUnreadPraises = (
  limit = 5
) =>
  api.get(
    `/praise/unread?limit=${limit}`
  );

// Chat Unread Count
export const getUnreadChatCount = () =>
  api.get(
    '/chat/unread-count'
  );

export const getTotalUnreadCount = () =>
  api.get(
    '/chat/unread-total'
  );

export const markChatRead = (
  teamId
) =>
  api.post(
    `/chat/mark-read/${teamId}`
  );

// Unified Badge Counts
export const getBadgeCounts = () =>
  api.get(
    '/badges/counts'
  );

export const markPraiseAsRead = (
  praiseId
) =>
  api.post(
    `/praise/mark-read/${praiseId}`
  );

export const markAllPraisesAsRead = () =>
  api.post(
    '/praise/mark-all-read'
  );

// Chat Channels V1
export const getChatChannels = (
  teamId,
  includeDm = true
) =>
  api.get(
    `/teams/${teamId}/chat/channels?include_dm=${includeDm}`
  );

export const provisionChatChannels = (
  teamId
) =>
  api.post(
    `/teams/${teamId}/chat/channels/provision`
  );

export const getChannelMessages = (
  channelId,
  params = {}
) => {
  const queryString =
    new URLSearchParams(
      params
    ).toString();

  return api.get(
    `/chat/channels/${channelId}/messages${
      queryString
        ? '?' + queryString
        : ''
    }`
  );
};

export const sendChannelMessage = (
  channelId,
  data
) =>
  api.post(
    `/chat/channels/${channelId}/messages`,
    data
  );

export const markChannelRead = (
  channelId
) =>
  api.post(
    `/chat/channels/${channelId}/read`
  );

export const getDMList = (
  teamId
) =>
  api.get(
    `/chat/dm/list${
      teamId
        ? `?team_id=${teamId}`
        : ''
    }`
  );

export const getDMContacts = (
  teamId
) =>
  api.get(
    `/teams/${teamId}/chat/dm-contacts`
  );

export const createOrGetDM = (
  toUserId,
  teamId
) =>
  api.post(
    `/chat/dm?to_user_id=${toUserId}${
      teamId
        ? `&team_id=${teamId}`
        : ''
    }`
  );

export const shareEventToChat = (
  teamId,
  eventId,
  eventType,
  channelKey = null
) =>
  api.post(
    `/teams/${teamId}/chat/share/event?event_id=${eventId}&event_type=${eventType}${
      channelKey
        ? '&channel_key=' + channelKey
        : ''
    }`
  );

export const shareDrillToChat = (
  teamId,
  drillId,
  channelKey = 'training'
) =>
  api.post(
    `/teams/${teamId}/chat/share/drill?drill_id=${drillId}&channel_key=${channelKey}`
  );

export const shareTacticToChat = (
  teamId,
  tacticId,
  channelKey = 'match'
) =>
  api.post(
    `/teams/${teamId}/chat/share/tactic?tactic_id=${tacticId}&channel_key=${channelKey}`
  );

export const updateEventAvailability = (
  messageId,
  status
) =>
  api.post(
    `/chat/messages/${messageId}/availability?status=${status}`
  );

export const getEventAvailability = (
  messageId
) =>
  api.get(
    `/chat/messages/${messageId}/availability`
  );

// Channel User Settings
export const getChannelUserSettings = (
  channelId
) =>
  api.get(
    `/chat/channels/${channelId}/settings`
  );

export const updateChannelUserSettings = (
  channelId,
  settings
) =>
  api.patch(
    `/chat/channels/${channelId}/settings`,
    settings
  );

// Team Chat Settings
export const getTeamChatSettings = (
  teamId
) =>
  api.get(
    `/teams/${teamId}/chat/settings`
  );

export const updateTeamChatSettings = (
  teamId,
  settings
) =>
  api.patch(
    `/teams/${teamId}/chat/settings`,
    settings
  );

// Chat Reactions
export const addMessageReaction = (
  messageId,
  emoji
) =>
  api.post(
    `/chat/messages/${messageId}/reactions?emoji=${encodeURIComponent(emoji)}`
  );

export const getMessageReactions = (
  messageId
) =>
  api.get(
    `/chat/messages/${messageId}/reactions`
  );

export const deleteMessage = (
  messageId
) =>
  api.delete(
    `/chat/messages/${messageId}`
  );

// Custom Channels
export const createCustomChannel = (
  teamId,
  data
) =>
  api.post(
    `/teams/${teamId}/chat/channels/custom`,
    data
  );

export const getCustomChannels = (
  teamId
) =>
  api.get(
    `/teams/${teamId}/chat/channels/custom`
  );

export const deleteCustomChannel = (
  channelId
) =>
  api.delete(
    `/chat/channels/${channelId}`
  );

export const updateChannelMembers = (
  channelId,
  memberIds
) =>
  api.patch(
    `/chat/channels/${channelId}/members`,
    memberIds
  );

// Chat File Upload
export const uploadChatAttachment = (
  file
) => {
  const formData =
    new FormData();

  formData.append(
    'file',
    file
  );

  return api.post(
    '/chat/upload',
    formData,
    {
      headers: {
        'Content-Type':
          'multipart/form-data',
      },
    }
  );
};

// Challenges
export const getChallenges = (
  teamId
) =>
  api.get(
    `/teams/${teamId}/challenges`
  );

export const getPlayerChallenges = (
  playerId
) =>
  api.get(
    `/players/${playerId}/challenges`
  );

// Evaluations
export const getPendingEvaluations = (
  teamId
) =>
  api.get(
    `/evaluations/pending?team_id=${teamId}`
  );

export const getPendingEvaluationsCount = (
  teamId
) =>
  api.get(
    `/evaluations/pending/count?team_id=${teamId}`
  );

export const getPendingAssessmentsCount = (
  teamId
) =>
  api.get(
    `/assessments/pending-count?team_id=${teamId}`
  );

export const getAssignedEvaluations = (
  teamId
) =>
  api.get(
    `/evaluations/player/assigned?team_id=${teamId}`
  );


// Support & Feedback
export const createSupportTicket = (
  data
) =>
  api.post(
    '/support/tickets',
    data
  );

export const sendErrorReport = (
  data
) =>
  api.post(
    '/support/tickets',
    {
      type: 'error_report',
      category: 'other',
      priority: 'high',
      ...data,
    }
  );

export const uploadTicketAttachment = (
  ticketId,
  file
) => {
  const formData =
    new FormData();

  formData.append(
    'file',
    file
  );

  return api.post(
    `/support/tickets/${ticketId}/attachments`,
    formData,
    {
      headers: {
        'Content-Type':
          'multipart/form-data',
      },
    }
  );
};

export const getMyTickets = (
  status = null
) => {
  const params =
    status
      ? `?status=${status}`
      : '';

  return api.get(
    `/support/tickets/my${params}`
  );
};

export const getTicket = (
  ticketId
) =>
  api.get(
    `/support/tickets/${ticketId}`
  );

export const addTicketComment = (
  ticketId,
  content,
  isInternal = false
) =>
  api.post(
    `/support/tickets/${ticketId}/comments`,
    {
      content,
      is_internal:
        isInternal,
    }
  );

// Support Admin
export const getAllTickets = (
  filters = {}
) => {
  const params =
    new URLSearchParams(
      filters
    ).toString();

  return api.get(
    `/support/admin/tickets${
      params
        ? '?' + params
        : ''
    }`
  );
};

export const updateTicket = (
  ticketId,
  data
) =>
  api.put(
    `/support/admin/tickets/${ticketId}`,
    data
  );

export const getTicketStats = () =>
  api.get(
    '/support/admin/tickets/stats'
  );

// Coach Dashboard
export const getCoachDashboard = (
  teamId,
  period = 'week'
) =>
  api.get(
    `/dashboard/coach?team_id=${teamId}&period=${period}`
  );

// Canonical V2 template health
export const getTemplatesV2Health = () =>
  api.get(
    '/templates/v2/health'
  );

// Lagstatus 2.0
export const getLagstatusV2 = (
  teamId,
  weeks = 4
) =>
  api.get(
    `/lagstatus/v2/${encodeURIComponent(teamId)}?weeks=${weeks}`
  );

export const getLagstatusV2Player = (
  teamId,
  playerId,
  weeks = 4
) =>
  api.get(
    `/lagstatus/v2/${encodeURIComponent(teamId)}/player/${encodeURIComponent(playerId)}?weeks=${weeks}`
  );

// Action Center
export const getActionCenter = (
  teamId,
  role
) =>
  api.get(
    `/action-center/${encodeURIComponent(teamId)}${
      role
        ? `?role=${role}`
        : ''
    }`
  );

export const getTeamPulse = (
  teamId,
  days = 7
) =>
  api.get(
    `/team-pulse/${encodeURIComponent(teamId)}?days=${days}`
  );

// Culture identity
export const getTeamIdentity = (
  teamId,
  days = 14
) =>
  api.get(
    `/team-identity/team/${encodeURIComponent(teamId)}?days=${days}`
  );

export const getPlayerIdentity = (
  teamId,
  playerId,
  days = 30
) =>
  api.get(
    `/team-identity/team/${encodeURIComponent(teamId)}/player/${encodeURIComponent(playerId)}?days=${days}`
  );

// Physical Tests
export const getTestSessions = (
  teamId,
  templateId = null
) =>
  api.get(
    `/teams/${encodeURIComponent(teamId)}/test-sessions${
      templateId
        ? `?template_id=${encodeURIComponent(templateId)}`
        : ''
    }`
  );

export const createTestSession = (
  teamId,
  data
) =>
  api.post(
    `/teams/${encodeURIComponent(teamId)}/test-sessions`,
    data
  );

export const getTestSession = (
  sessionId
) =>
  api.get(
    `/test-sessions/${encodeURIComponent(sessionId)}`
  );

export const updateTestSession = (
  sessionId,
  data
) =>
  api.patch(
    `/test-sessions/${encodeURIComponent(sessionId)}`,
    data
  );

export const deleteTestSession = (
  sessionId
) =>
  api.delete(
    `/test-sessions/${encodeURIComponent(sessionId)}`
  );

// Individual Training
export const listIndividualTrainingPlans = (
  teamId,
  status
) =>
  api.get(
    `/teams/${encodeURIComponent(teamId)}/individual-training/plans`,
    {
      params:
        status
          ? { status }
          : {},
    }
  );

export const createIndividualTrainingPlan = (
  teamId,
  data
) =>
  api.post(
    `/teams/${encodeURIComponent(teamId)}/individual-training/plans`,
    data
  );

// Development Focus → Individual Training
export const createIndividualTrainingPlanFromFocus = (
  teamId,
  focusId
) =>
  api.post(
    `/teams/${encodeURIComponent(teamId)}/individual-training/from-focus/${encodeURIComponent(focusId)}`
  );

export const getIndividualTrainingPlan = (
  planId
) =>
  api.get(
    `/individual-training/plans/${planId}`
  );

export const updateIndividualTrainingPlan = (
  planId,
  data
) =>
  api.put(
    `/individual-training/plans/${planId}`,
    data
  );

export const publishIndividualTrainingPlan = (
  planId
) =>
  api.post(
    `/individual-training/plans/${planId}/publish`
  );

export const archiveIndividualTrainingPlan = (
  planId
) =>
  api.post(
    `/individual-training/plans/${planId}/archive`
  );

export const duplicateIndividualTrainingPlan = (
  planId
) =>
  api.post(
    `/individual-training/plans/${planId}/duplicate`
  );

export const getMyIndividualTrainingPlans = () =>
  api.get(
    '/me/individual-training/plans'
  );

export const getMyIndividualTrainingCalendar = (
  params = {}
) =>
  api.get(
    '/me/individual-training/calendar',
    {
      params,
    }
  );

export const setIndividualTrainingCompletion = (
  sessionId,
  data
) =>
  api.post(
    `/individual-training/sessions/${sessionId}/completion`,
    data
  );

// ============================================================================
// DEVELOPMENT FOCUS
// Canonical player-development layer
//
// Signal
// → Development Focus
// → Development Actions
// → Evidence
// → Review
// → Continue / Adjust / Complete
// ============================================================================

export const listPlayerDevelopmentFocuses = (
  teamId,
  playerId,
  status = null
) =>
  api.get(
    `/development-focus/teams/${encodeURIComponent(teamId)}/players/${encodeURIComponent(playerId)}`,
    {
      params:
        status
          ? { status }
          : {},
    }
  );

// Signal Inbox — coach decides whether real source data is relevant.
export const listPlayerDevelopmentSignals = (
  teamId,
  playerId,
  days = 30,
  limit = 20
) =>
  api.get(
    `/development-focus/teams/${encodeURIComponent(teamId)}/players/${encodeURIComponent(playerId)}/signals`,
    { params: { days, limit } }
  );

export const linkDevelopmentSignalToFocus = (
  teamId,
  playerId,
  data
) =>
  api.post(
    `/development-focus/teams/${encodeURIComponent(teamId)}/players/${encodeURIComponent(playerId)}/signals/link`,
    data
  );

export const dismissDevelopmentSignal = (
  teamId,
  playerId,
  data
) =>
  api.post(
    `/development-focus/teams/${encodeURIComponent(teamId)}/players/${encodeURIComponent(playerId)}/signals/dismiss`,
    data
  );

export const createDevelopmentFocusFromSignal = (
  teamId,
  playerId,
  data
) =>
  api.post(
    `/development-focus/teams/${encodeURIComponent(teamId)}/players/${encodeURIComponent(playerId)}/signals/create-focus`,
    data
  );

export const getDevelopmentFocus = (
  focusId
) =>
  api.get(
    `/development-focus/${encodeURIComponent(focusId)}`
  );

export const getFocusQuestionnaires = (focusId) =>
  api.get(`/development-focus/${encodeURIComponent(focusId)}/questionnaires`);

export const updateFocusQuestionnaires = (focusId, data) =>
  api.put(`/development-focus/${encodeURIComponent(focusId)}/questionnaires`, data);

export const createPlayerDevelopmentFocus = (
  teamId,
  playerId,
  data
) =>
  api.post(
    `/development-focus/teams/${encodeURIComponent(teamId)}/players/${encodeURIComponent(playerId)}`,
    data
  );

export const updateDevelopmentFocus = (
  focusId,
  data
) =>
  api.patch(
    `/development-focus/${encodeURIComponent(focusId)}`,
    data
  );

export const archiveDevelopmentFocus = (
  focusId
) =>
  api.delete(
    `/development-focus/${encodeURIComponent(focusId)}`
  );

// Development Actions
export const createDevelopmentFocusAction = (
  focusId,
  data
) =>
  api.post(
    `/development-focus/${encodeURIComponent(focusId)}/actions`,
    data
  );

export const updateDevelopmentFocusAction = (
  focusId,
  actionId,
  data
) =>
  api.patch(
    `/development-focus/${encodeURIComponent(focusId)}/actions/${encodeURIComponent(actionId)}`,
    data
  );

// Individual Training <-> Development Focus
//
// Individual Training remains source of truth for:
// - exercises
// - schedule
// - completion
//
// Development Focus only owns the development relationship/context.
export const linkIndividualTrainingPlanToFocus = (
  focusId,
  data
) =>
  api.post(
    `/development-focus/${encodeURIComponent(focusId)}/individual-training`,
    data
  );

export const listFocusIndividualTrainingOptions = (focusId, params = {}) =>
  api.get(`/development-focus/${encodeURIComponent(focusId)}/individual-training-options`, { params });

export const unlinkIndividualTrainingPlanFromFocus = (
  focusId,
  planId
) =>
  api.delete(
    `/development-focus/${encodeURIComponent(focusId)}/individual-training/${encodeURIComponent(planId)}`
  );

// Evidence
export const linkDevelopmentFocusEvidence = (
  focusId,
  data
) =>
  api.post(
    `/development-focus/${encodeURIComponent(focusId)}/evidence`,
    data
  );

export const unlinkDevelopmentFocusEvidence = (
  focusId,
  evidenceId
) =>
  api.delete(
    `/development-focus/${encodeURIComponent(focusId)}/evidence/${encodeURIComponent(evidenceId)}`
  );

// Review
export const reviewDevelopmentFocus = (
  focusId,
  data
) =>
  api.post(
    `/development-focus/${encodeURIComponent(focusId)}/review`,
    data
  );

// Development Conversation → Development Focus
export const createDevelopmentFocusFromConversation = (
  conversationId,
  data = {}
) =>
  api.post(
    `/development-focus/from-conversation/${encodeURIComponent(conversationId)}`,
    data
  );

export default api;
// Verified change of an existing login email; first-email setup remains separate.
export const requestLoginEmailChange = (candidateEmail) => api.post('/auth/me/email-change', {
  candidate_email: candidateEmail, return_origin: window.location.origin,
});
export const confirmLoginEmailChange = (token) => api.post('/auth/email-change/confirm', { token });


export const requestPlayerLoginEmailChange = (teamId, profileId, payload) =>
  api.post(`/teams/${teamId}/roster/${profileId}/login-email-change`, {
    ...payload, return_origin: window.location.origin,
  });

export const getPlayerAccountEmail = (teamId, profileId) =>
  api.get(`/teams/${teamId}/roster/${profileId}/account-email`);

export const previewInvitationRecipient = (id) => api.get(`/invites/personal/${id}/recipient`);
export const sendPlayerLoginLink = (teamId, profileId, expectedEmail) =>
  api.post(`/teams/${teamId}/roster/${profileId}/send-login-link`, {}, { params: { expected_email: expectedEmail } });

// P24: canonical membership/invitation recipients, with a confirmed preview.
export const previewMemberEmail = (teamId, target) =>
  api.post(`/teams/${teamId}/member-email/preview`, target);
export const sendMemberEmail = (teamId, data) =>
  api.post(`/teams/${teamId}/member-email/send`, data);

// Email codes are verified in the browser/PWA that requested them.
export const requestEmailCode = (email, purpose) => api.post(
  purpose === 'reset' ? '/auth/forgot-password' : '/auth/magic-link/request',
  { email, delivery: 'code', ...(purpose === 'login' ? { return_origin: window.location.origin } : {}) }
);
export const verifyEmailCode = (challenge_id, code, purpose) =>
  api.post('/auth/email-code/verify', { challenge_id, code, purpose });
